import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Card,
  Heading,
  Input,
  RadioGroup,
  Stack,
  Radio,
  Button,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Grid,
  GridItem,
  useBreakpointValue,
  VStack,
  HStack,
  Text,
  Select,
  Textarea,
  IconButton,
  Flex,
  FormControl,
  FormErrorMessage,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
} from "@chakra-ui/react";
import axios from 'axios';
import { useLocation, useNavigate } from "react-router-dom";
import { get_vehicle_list } from "../../redux/vehiclSlice";
import { useDispatch, useSelector } from "react-redux";
import { get_defective_returns, get_defective_return_detail } from "../../redux/DefectiveSlice";import { X, ArrowLeft, Building, CreditCard, Wallet } from "lucide-react";
import * as Yup from "yup";
import { useFormik } from "formik";
import RefundProcess from "./RefundProcess";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Yup validation schema
const DefectiveReturnSchema = Yup.object().shape({
  vendor_name: Yup.string()
    .required("Customer name is required")
    .test("not-empty", "Customer name cannot be empty", value => value && value.trim().length > 0),

  purchase_invoice: Yup.string()
    .required("Purchase invoice is required")
    .test("not-empty", "Purchase invoice cannot be empty", value => value && value.trim().length > 0),

  invoice_date: Yup.string().required("Invoice date is required"),
  total_amount: Yup.number()
    .required("Total amount is required")
    .min(0.01, "Total amount must be greater than 0"),
  narration: Yup.string()
    .required("Narration is required")
    .test("not-empty", "Narration cannot be empty", value => value && value.trim().length > 0),

  return_date: Yup.string().required("Return date is required"),
  products: Yup.array().of(
    Yup.object().shape({
      product: Yup.string().required("Product is required"),
      capacity: Yup.number()
        .required("Capacity is required")
        .min(0.01, "Capacity must be greater than 0"), // Changed from 0 to 0.01

      reason: Yup.string().required("Reason is required"),
      taxable_amount: Yup.number()
        .required("Taxable amount is required")
        .min(0, "Taxable amount cannot be negative"),

      amount: Yup.number()
        .required("Amount is required")
        .min(0, "Amount cannot be negative"),
      vehicle_id: Yup.string().required("Vehicle is required"),
      vehicle_number: Yup.string().required("Vehicle number is required"),
      id: Yup.string(),
      prod_code: Yup.string(),
    })
  ),
});

const EditDefectiveReturn = () => {
  const location = useLocation();
  const { state } = useLocation();
  const { returnData, returnId } = location.state || {};
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [currentReturnData, setCurrentReturnData] = useState(null);
  const [calculatedTotalAmount, setCalculatedTotalAmount] = useState("");
  const [selectedMode, setSelectedMode] = useState("");
  const [initialRefundStep, setInitialRefundStep] = useState("method");
  const [isOriginalCredit, setIsOriginalCredit] = useState(false);
  const [isRefundCompleted, setIsRefundCompleted] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);
  const cancelRef = useRef();
  const [selectedPaymentMode, setSelectedPaymentMode] = useState(null);
  const [selectedBankId, setSelectedBankId] = useState(null);
  const [transactionNumber, setTransactionNumber] = useState("");
  const [transactionMode, setTransactionMode] = useState(null);
  const [paymentNotes, setPaymentNotes] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { vehicle_list, loading } = useSelector((state) => state.vehicles);
  const { defective_returns, total_count, defective_return_detail, detail_loading } = useSelector(
    (state) => state.defectiveReturns
  );

  useEffect(() => {
    // Check if coming from refund completion
    if (state?.fromRefund) {
      setIsRefundCompleted(true);
    }
  }, [state]);

  const [mainId, setMainId] = useState("");
  const toast = useToast();
  const gridColumns = useBreakpointValue({ base: 1, sm: 2, md: 3, lg: 4 });

  const formik = useFormik({
    initialValues: {
      vendor_name: "",
      purchase_invoice: "",
      invoice_date: "",
      total_amount: "",
      narration: "",
      return_date: "",
      products: [
        {
          product: "",
          capacity: "",
          reason: "",
          // unit_rate: "",
          // gst_percent: "",
          // return_quantity: "",
          // cyl_gross_weight: "",
          // cyl_tare_weight: "",
          // cyl_serial_no: "",
          // net_weight: "",
          taxable_amount: "",
          // igst_amount: "",
          // cgst_amount: "",
          // sgst_amount: "",
          amount: "",
          vehicle_id: "",
          vehicle_number: "",
          id: "",
          prod_code: "",
        },
      ],
    },
    validationSchema: DefectiveReturnSchema,
    onSubmit: async (values) => {
      // Check if calculate has been clicked
      if (!calculatedTotalAmount || calculatedTotalAmount === "0.00") {
        toast({
          title: "Error",
          description: "Please calculate the total amount first",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        return;
      }

      setIsPopupOpen(true);
    },
  });

  useEffect(() => {
    console.log("Location state:", location.state);
    console.log("returnData:", returnData);
    console.log("returnId:", returnId);
    
    // Extract the ID from wherever it exists
    const actualId = returnData?.id || (typeof returnId === 'object' ? returnId?.id : returnId);
    
    if (actualId) {
      console.log("Calling API with ID:", actualId);
      dispatch(get_defective_return_detail({ id: actualId, type: "2" }))
        .unwrap()
        .then((res) => {
          console.log("API Response:", res);
          
          if (res.success && res.data) {
            setCurrentReturnData(res.data);
            loadReturnData(res.data);
          } else {
            toast({
              title: "Error",
              description: res.message || "Defective return record not found",
              status: "error",
              duration: 3000,
              isClosable: true,
              position: "top",
            });
          }
        })
        .catch((err) => {
          console.error("Error fetching defective return:", err);
          toast({
            title: "Error",
            description: err.message || "Failed to fetch defective return data",
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "top",
          });
        });
    } else {
      console.log("No ID found in returnData or returnId");
      toast({
        title: "Error",
        description: "No return ID found",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    }
  }, [returnData, returnId, dispatch, toast]);
      
  const loadReturnData = (returnData) => {
    setMainId(returnData.id || "");

    // Check if original payment mode is credit
    const isCredit = returnData.paymentmode === "CREDIT";
    setIsOriginalCredit(isCredit);

    const products = returnData.details?.map((detail) => {
      return {
        product: detail.product_name || "",
        reason: detail.type || detail.reason || "",
        capacity: detail.capacity || "",
        unit_rate: detail.unit_rate,
        // Fix: Store GST as numeric value, not string with %
        gst_percent: detail.gst || "",
        return_quantity: detail.qty || "",
        cyl_gross_weight: detail.cyl_gross_weight,
        cyl_tare_weight: detail.cyl_tare_weight,
        cyl_serial_no: detail.serial_number || "",
        net_weight: detail.net_weight || "",
        taxable_amount: detail.taxable_amount || "",
        igst_amount: detail.igst_amount || "",
        cgst_amount: detail.cgst_amount || "",
        sgst_amount: detail.sgst_amount || "",
        amount: detail.total_amount || "",
        vehicle_id: detail.vehicleid || "",
        vehicle_number: detail.vehicleno || "",
        id: detail.id || "",
        prod_code: detail.productid || "",
      };
    });

    formik.setValues({
      vendor_name: returnData.customer_name || "",
      purchase_invoice: returnData.sr_no || "",
      sr_date: returnData.sr_date || "",
      invoice_date: returnData.sr_date || returnData.inv_date || "",
      total_amount: returnData.total_amount || "",
      narration: returnData.narration || returnData.description || "",
      return_date: returnData.inv_date || "",
      products: products?.length > 0 ? products : formik.values.products,
    });
  };

  const handleInputChange = (e, index, field) => {
    const { value } = e.target;

    if (index !== undefined) {
      const updatedProducts = [...formik.values.products];
      updatedProducts[index][field] = value;

      if (field === "cyl_gross_weight" || field === "cyl_tare_weight") {
        const gross = Number(updatedProducts[index].cyl_gross_weight) || 0;
        const tare = Number(updatedProducts[index].cyl_tare_weight) || 0;
        updatedProducts[index].net_weight =
          gross && tare ? (gross - tare).toFixed(2) : "";
      }

      formik.setFieldValue("products", updatedProducts);
    } else {
      formik.setFieldValue(field, value);
    }
  };


  const handleSaveRefund = async () => {
    if (isOriginalCredit) {
      // For credit, show confirmation
      setConfirmationData({
        mode: 'credit',
        paymentMode: 5,
        bankId: null,
        transactionNum: null
      });
      setShowConfirmation(true);
      return;
    }

    // For other payment modes, prepare confirmation data
    const paymentModeMap = {
      cash: 1,
      online: 3,
      wallet: 4
    };

    const paymentMode = paymentModeMap[selectedPaymentMode];

    if (!paymentMode) {
      toast({
        title: "Error",
        description: "Please select a payment method",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    // Show confirmation dialog
    setConfirmationData({
      mode: selectedPaymentMode,
      paymentMode: paymentMode,
      bankId: selectedBankId,
      transactionNum: transactionNumber
    });
    setShowConfirmation(true);
  };

  const submitRefundApi = async (paymentMode, bankId = null, transactionNum = null) => {
    setIsProcessing(true);

    try {
      const payload = new FormData();

      // Basic fields
      payload.append('action', 'update');
      payload.append('userid', currentReturnData?.customerid || '');
      payload.append('id', currentReturnData?.id || '');
      payload.append('totalamount', calculatedTotalAmount);
      payload.append('narration', formik.values.narration || "NA");
      payload.append('inv_date', formik.values.return_date || new Date().toISOString().split('T')[0]);
      payload.append('payment_mode', paymentMode);
      payload.append('amount', calculatedTotalAmount);
      payload.append('bank_id', bankId || "");
      payload.append('narration', paymentNotes || "NA");

      // Add transaction details for cash (1) or online (3) payments
      if (paymentMode === 1 || paymentMode === 3) {
        payload.append('transaction_number', transactionNum || "NA");
        payload.append('mode_of_transaction', transactionMode || 1);
      }

      // Add product details
      if (formik.values.products && formik.values.products.length > 0) {
        formik.values.products.forEach((product, index) => {
          payload.append(`prod_code[${index}]`, product.prod_code || "NA");
          payload.append(`details_id[${index}]`, product.id);
          payload.append(`quantity[${index}]`, product.return_quantity || 0);
          payload.append(`unit_rate[${index}]`, product.unit_rate || 0);
          payload.append(`net_weight[${index}]`, product.net_weight || 0);
          payload.append(`taxable_amount[${index}]`, product.taxable_amount || 0);
          payload.append(`gst[${index}]`, product.gst_percent || 0);
          payload.append(`igst_amount[${index}]`, product.igst_amount || 0);
          payload.append(`cgst_amount[${index}]`, product.cgst_amount || 0);
          payload.append(`sgst_amount[${index}]`, product.sgst_amount || 0);
          payload.append(`total_amount[${index}]`, product.amount || 0);
          payload.append(`vehicle_id[${index}]`, product.vehicle_id || "");
          payload.append(`reason[${index}]`, product.reason || "NA");
          payload.append(`cyl_tare_weight[${index}]`, product.cyl_tare_weight || 0);
          payload.append(`cyl_gross_weight[${index}]`, product.cyl_gross_weight || 0);
        });
      }

      const response = await axios.post(
        'https://gaswale.vensframe.com/api/admin/defective_return.php',
        payload,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data && response.data.success) {
        toast({
          title: "Success",
          description: "Refund processed successfully!",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        navigate("/defective-return");
      } else {
        throw new Error(response.data?.message || "Failed to process refund");
      }
    } catch (error) {
      console.error("Refund API error:", error);

      let errorMessage = "Failed to process refund";
      if (error.response) {
        errorMessage = error.response.data?.message || `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = "Network error. Please check your connection.";
      } else {
        errorMessage = error.message;
      }

      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmRefund = async () => {
    setShowConfirmation(false);

    if (!confirmationData) return;

    const { paymentMode, bankId, transactionNum } = confirmationData;

    if (confirmationData.mode === 'credit') {
      await handleCreditSubmit();
    } else {
      await submitRefundApi(paymentMode, bankId, transactionNum);
    }
  };

  const handleCancelRefund = () => {
    setShowConfirmation(false);
    setConfirmationData(null);
  };

  useEffect(() => {
    dispatch(get_vehicle_list({ page: 1, rows: 100 }));
  }, [dispatch]);

  const removeProductRow = (index) => {
    if (formik.values.products.length > 1) {
      const updatedProducts = [...formik.values.products];
      updatedProducts.splice(index, 1);
      formik.setFieldValue("products", updatedProducts);
    }
  };

  const handleCalculateAll = async () => {
    // Create validation errors object for user-editable fields only
    const validationErrors = {};
    let hasErrors = false;

    // Validate main form fields (user-editable)
    if (!formik.values.narration || formik.values.narration.trim() === '') {
      validationErrors.narration = "Narration is required";
      hasErrors = true;
    }

    if (!formik.values.return_date) {
      validationErrors.return_date = "Return date is required";
      hasErrors = true;
    }

    // Validate user-editable product fields only
    const productErrors = [];
    formik.values.products.forEach((product, index) => {
      const productError = {};

      // Only validate user-editable fields
      if (!product.capacity || parseFloat(product.capacity) <= 0) {
        productError.capacity = "Capacity is required and must be greater than 0";
        hasErrors = true;
      }

      // if (!product.cyl_gross_weight || parseFloat(product.cyl_gross_weight) <= 0) {
      //   productError.cyl_gross_weight = "Gross weight is required and must be greater than 0";
      //   hasErrors = true;
      // }

      // if (!product.cyl_tare_weight || parseFloat(product.cyl_tare_weight) <= 0) {
      //   productError.cyl_tare_weight = "Tare weight is required and must be greater than 0";
      //   hasErrors = true;
      // }

      // Validate tare weight is less than gross weight
      // if (parseFloat(product.cyl_tare_weight) >= parseFloat(product.cyl_gross_weight)) {
      //   productError.cyl_tare_weight = "Tare weight must be less than gross weight";
      //   hasErrors = true;
      // }

      // if (!product.cyl_serial_no || product.cyl_serial_no.trim() === '') {
      //   productError.cyl_serial_no = "Serial number is required";
      //   hasErrors = true;
      // }

      if (!product.vehicle_number || product.vehicle_number.trim() === '') {
        productError.vehicle_number = "Vehicle number is required";
        hasErrors = true;
      }

      if (Object.keys(productError).length > 0) {
        productErrors[index] = productError;
      }
    });

    if (Object.keys(productErrors).length > 0) {
      validationErrors.products = productErrors;
    }

    // If there are validation errors, show them and return
    if (hasErrors) {
      formik.setErrors(validationErrors);

      // Set touched for the fields we validated
      const touched = {
        narration: true,
        return_date: true,
        products: formik.values.products.map(() => ({
          capacity: true,
          vehicle_number: true,
        }))
      };
      formik.setTouched(touched);

      toast({
        title: "Validation Error",
        description: "Please fix all required fields before calculating",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    // Clear any previous errors
    formik.setErrors({});

    // Proceed with calculations if validation passes
    const currentProducts = [...formik.values.products];

    currentProducts.forEach((product, index) => {
      const unitRate = parseFloat(product.unit_rate) || 0;
      const capacity = parseFloat(product.capacity) || 1;
      const netWeight = parseFloat(product.net_weight) || 0;

      // Remove % symbol from GST if present
      const gstPercent = parseFloat(String(product.gst_percent).replace('%', '')) || 0;

      // Step 1: Calculate base price (unit rate without GST)
      const basePricePerUnit = unitRate / (1 + (gstPercent / 100));

      // Step 2: Calculate GST amount per unit
      const gstAmountPerUnit = unitRate - basePricePerUnit;

      // Step 3: Calculate per kg price (without GST)
      const perKgPrice = basePricePerUnit / capacity;

      // Step 4: Calculate taxable amount based on net weight
      const taxableAmount = perKgPrice * netWeight;

      // Step 5: Calculate total GST for the net weight
      const totalGstAmount = taxableAmount * (gstPercent / 100);

      // Step 6: Calculate final amount
      const finalAmount = taxableAmount + totalGstAmount;

      // Set the values
      currentProducts[index].taxable_amount = taxableAmount.toFixed(2);
      currentProducts[index].cgst_amount = (totalGstAmount / 2).toFixed(2);
      currentProducts[index].sgst_amount = (totalGstAmount / 2).toFixed(2);
      currentProducts[index].igst_amount = "0.00";
      currentProducts[index].amount = finalAmount.toFixed(2);
    });

    // Update formik with all calculated products at once
    formik.setFieldValue("products", currentProducts);

    // Calculate total amount
    const totalAmount = currentProducts.reduce((sum, product) => sum + (parseFloat(product.amount) || 0), 0);
    const formattedTotalAmount = totalAmount.toFixed(2);

    // Update total amount in formik
    formik.setFieldValue("total_amount", formattedTotalAmount);

    // Set the calculated total amount to state
    setCalculatedTotalAmount(formattedTotalAmount);

    toast({
      title: "Success",
      description: "Calculations completed successfully",
      status: "success",
      duration: 2000,
      isClosable: true,
      position: "top",
    });
  };

  const handleRefundClick = (method) => {
    // Check if calculate has been clicked
    if (!calculatedTotalAmount || calculatedTotalAmount === "0.00") {
      toast({
        title: "Error",
        description: "Please calculate the total amount first",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    // Set the selected mode and initial step based on the selection
    setSelectedMode(method);

    if (method === "wallet") {
      setInitialRefundStep("wallet");
    } else if (method === "cash") {
      setInitialRefundStep("cash");
    } else if (method === "online") {
      setInitialRefundStep("online");
    } else if (method === "credit") {
      setInitialRefundStep("credit"); // if you have this option
    } else {
      setInitialRefundStep("method"); // fallback to method selection
    }

    setIsPopupOpen(true); // open popup
  };

  const handleSubmit = async () => {
    // Validate form
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      formik.setErrors(errors);
      toast({
        title: "Validation Error",
        description: "Please fix all validation errors before submitting",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    // Check if calculate has been clicked
    if (!calculatedTotalAmount || calculatedTotalAmount === "0.00") {
      toast({
        title: "Error",
        description: "Please calculate the total amount first",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    // If original payment mode was credit, process directly without popup
    if (isOriginalCredit) {
      handleCreditSubmit();
      return;
    }

    // For other payment modes  , open popup
    setIsPopupOpen(true);
  };

  const handleCreditSubmit = async () => {
    setIsProcessing(true);

    try {
      // Import axios at the top of your file if not already imported
      // import axios from 'axios';

      const payload = new FormData();
      payload.append('action', 'update');
      payload.append('userid', returnData?.customerid || '');
      payload.append('id', returnData?.id || '');
      payload.append('totalamount', calculatedTotalAmount);
      payload.append('narration', formik.values.narration || "Credit Refund");
      payload.append('inv_date', formik.values.return_date || new Date().toISOString().split('T')[0]);
      payload.append('payment_mode', 5); // Credit mode
      payload.append('amount', calculatedTotalAmount);
      payload.append('bank_id', "");

      // Add product details
      if (formik.values.products && formik.values.products.length > 0) {
        formik.values.products.forEach((product, index) => {
          payload.append(`prod_code[${index}]`, product.prod_code || "NA");
          payload.append(`details_id[${index}]`, product.id);
          payload.append(`quantity[${index}]`, product.return_quantity || 0);
          payload.append(`unit_rate[${index}]`, product.unit_rate || 0);
          payload.append(`net_weight[${index}]`, product.net_weight || 0);
          payload.append(`taxable_amount[${index}]`, product.taxable_amount || 0);
          payload.append(`gst[${index}]`, product.gst_percent || 0);
          payload.append(`igst_amount[${index}]`, product.igst_amount || 0);
          payload.append(`cgst_amount[${index}]`, product.cgst_amount || 0);
          payload.append(`sgst_amount[${index}]`, product.sgst_amount || 0);
          payload.append(`total_amount[${index}]`, product.amount || 0);
          payload.append(`vehicle_id[${index}]`, product.vehicle_id || "");
          payload.append(`reason[${index}]`, product.reason || "NA");
          payload.append(`cyl_tare_weight[${index}]`, product.cyl_tare_weight || 0);
          payload.append(`cyl_gross_weight[${index}]`, product.cyl_gross_weight || 0);
        });
      }

      const response = await axios.post(
        'https://gaswale.vensframe.com/api/admin/defective_return.php',
        payload,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data && response.data.success) {
        toast({
          title: "Success",
          description: "Credit refund processed successfully!",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        navigate("/defective-return");
      } else {
        throw new Error(response.data?.message || "Failed to process credit refund");
      }
    } catch (error) {
      console.error("Credit refund error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to process credit refund",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setIsProcessing(false); // Change from setSaving(false)
    }
  };

  return (
    <Box p={6} bg="gray.50" minH="100vh">
      <Card p={8} borderRadius="xl" shadow="lg" bg="white">
        <VStack spacing={6} w="100%">
          <HStack w="100%" justify="space-between">
            <HStack spacing={3}>
              <IconButton
                icon={<ArrowLeft size={20} />}
                onClick={() =>
                  navigate("/defective-return", {
                    state: {
                      fromDetails: true,
                      page: state?.page || 1,
                      rows: state?.rows || 5,
                      initialValue: state?.initialValue || 1
                    }
                  })
                }
                aria-label="Go Back"
                variant="ghost"
                colorScheme="blue"
              />
              <Heading
                fontSize="2xl"
                textAlign="center"
                color="gray.700"
                fontWeight="600"
              >
                EDIT DEFECTIVE/PURCHASE RETURN DATA
              </Heading>
            </HStack>
          </HStack>

          <Grid templateColumns={`repeat(${gridColumns}, 1fr)`} gap={6} w="100%">
            <GridItem>
              <FormControl isInvalid={formik.errors.vendor_name && formik.touched.vendor_name}>
                <VStack align="start" spacing={2}>
                  <Text fontSize="sm" fontWeight="500" color="gray.600">Customer Name</Text>
                  <Input
                    placeholder="customer name"
                    value={formik.values.vendor_name}
                    onChange={(e) => handleInputChange(e, undefined, "vendor_name")}
                    onBlur={formik.handleBlur}
                    size="md"
                    bg="white"
                    readOnly
                    borderColor="gray.300"
                    name="vendor_name"
                  />
                </VStack>
                <FormErrorMessage>{formik.errors.vendor_name}</FormErrorMessage>
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl isInvalid={formik.errors.purchase_invoice && formik.touched.purchase_invoice}>
                <VStack align="start" spacing={2}>
                  <Text fontSize="sm" fontWeight="500" color="gray.600">Purchase Invoice</Text>
                  <Input
                    placeholder="Purchase Invoice"
                    value={formik.values.purchase_invoice}
                    onChange={(e) => handleInputChange(e, undefined, "purchase_invoice")}
                    onBlur={formik.handleBlur}
                    size="md"
                    bg="white"
                    borderColor="gray.300"
                    name="purchase_invoice"
                  />
                </VStack>
                <FormErrorMessage>{formik.errors.purchase_invoice}</FormErrorMessage>
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl isInvalid={formik.errors.invoice_date && formik.touched.invoice_date}>
                <VStack align="start" spacing={2}>
                  <Text fontSize="sm" fontWeight="500" color="gray.600">Ref Invoice Date</Text>
                  <Text fontSize="md" color="gray.800">
                    {formik.values?.sr_date}
                  </Text>
                </VStack>
                <FormErrorMessage>{formik.errors.invoice_date}</FormErrorMessage>
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl isInvalid={formik.errors.total_amount && formik.touched.total_amount}>
                <VStack align="start" spacing={2}>
                  <Text fontSize="sm" fontWeight="500" color="gray.600">Total Amount</Text>
                  <Input
                    placeholder="Total Amount"
                    value={calculatedTotalAmount || formik.values.total_amount}
                    onChange={(e) => handleInputChange(e, undefined, "total_amount")}
                    onBlur={formik.handleBlur}
                    readOnly
                    size="md"
                    bg="gray.50"
                    borderColor="gray.300"
                    fontWeight="600"
                    name="total_amount"
                  />
                  {calculatedTotalAmount && (
                    <Text fontSize="xs" color="green.500">
                      Calculated: ₹{calculatedTotalAmount}
                    </Text>
                  )}
                </VStack>
                <FormErrorMessage>{formik.errors.total_amount}</FormErrorMessage>
              </FormControl>
            </GridItem>
          </Grid>

          <Grid templateColumns={`repeat(${Math.min(gridColumns, 2)}, 1fr)`} gap={6} w="100%">
            <GridItem colSpan={2}>
              <HStack align="start" spacing={6} w="100%">
                {/* Narration */}
                <FormControl
                  isInvalid={formik.errors.narration && formik.touched.narration}
                  w="50%"
                >
                  <VStack align="start" spacing={2} w="100%">
                    <Text fontSize="sm" fontWeight="500" color="gray.600">Narration *</Text>
                    <Input
                      placeholder="Narration"
                      value={formik.values.narration}
                      onChange={(e) => handleInputChange(e, undefined, "narration")}
                      onBlur={formik.handleBlur}
                      size="md"
                      bg="white"
                      borderColor="gray.300"
                      isRequired
                      name="narration"
                    />
                  </VStack>
                  <FormErrorMessage>{formik.errors.narration}</FormErrorMessage>
                </FormControl>

                {/* Return Date */}
              {/* Return Date */}
<FormControl
  isInvalid={formik.errors.return_date && formik.touched.return_date}
  w="50%"
>
  <VStack align="start" spacing={2} w="100%">
    <Text fontSize="sm" fontWeight="500" color="gray.600">Return Date</Text>
    <Box w="100%">
      <DatePicker
        selected={formik.values.return_date ? new Date(formik.values.return_date) : null}
        onChange={(date) => {
          if (!date) {
            handleInputChange(
              { target: { value: '' } },
              undefined,
              "return_date"
            );
            return;
          }
          
          // Format date as YYYY-MM-DD in local timezone to avoid off-by-one errors
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const formattedDate = `${year}-${month}-${day}`;
          
          handleInputChange(
            { target: { value: formattedDate } },
            undefined,
            "return_date"
          );
        }}
        minDate={formik.values.invoice_date ? new Date(formik.values.invoice_date) : 
                formik.values.sr_date ? new Date(formik.values.sr_date) : null}
        maxDate={new Date()} // This allows today's date
        dateFormat="dd/MM/yyyy"
        placeholderText="Select Return Date"
        customInput={
          <Input
            size="md"
            bg="white"
            borderColor="gray.300"
            name="return_date"
          />
        }
        filterDate={(date) => {
          const minDate = formik.values.invoice_date ? new Date(formik.values.invoice_date) : 
                         formik.values.sr_date ? new Date(formik.values.sr_date) : null;
          
          if (!minDate) return true;
          
          // Normalize all dates to start of day for accurate comparison
          const normalizeDate = (d) => {
            const normalized = new Date(d);
            normalized.setHours(0, 0, 0, 0);
            return normalized;
          };
          
          const selectedDate = normalizeDate(date);
          const minDateNormalized = normalizeDate(minDate);
          const maxDateNormalized = normalizeDate(new Date());
          
          return selectedDate >= minDateNormalized && selectedDate <= maxDateNormalized;
        }}
      />
    </Box>
  </VStack>
  <FormErrorMessage>{formik.errors.return_date}</FormErrorMessage>
</FormControl>
              </HStack>
            </GridItem>
          </Grid>


          <Box w="100%" bg="white" borderRadius="lg" shadow="sm" border="1px" borderColor="gray.200">
            <Box overflowX="auto">
              <Table variant="simple" size="md">
                <Thead>
                  <Tr bg="blue.600">
                    <Th color="white" py={4} px={4} fontSize="sm" fontWeight="600" textTransform="uppercase" letterSpacing="wide">Product</Th>
                    <Th color="white" py={4} px={4} fontSize="sm" fontWeight="600" textTransform="uppercase" letterSpacing="wide">Reason</Th>
                    <Th color="white" py={4} px={4} fontSize="sm" fontWeight="600" textTransform="uppercase" letterSpacing="wide">Capacity</Th>
                    <Th color="white" py={4} px={4} fontSize="sm" fontWeight="600" textTransform="uppercase" letterSpacing="wide">Unit Rate</Th>
                    <Th color="white" py={4} px={4} fontSize="sm" fontWeight="600" textTransform="uppercase" letterSpacing="wide">GST %</Th>
                    <Th color="white" py={4} px={4} fontSize="sm" fontWeight="600" textTransform="uppercase" letterSpacing="wide">Return Qty</Th>
                    <Th color="white" py={4} px={4} fontSize="sm" fontWeight="600" textTransform="uppercase" letterSpacing="wide">Cyl. Gross Weight</Th>
                    <Th color="white" py={4} px={4} fontSize="sm" fontWeight="600" textTransform="uppercase" letterSpacing="wide">Cyl. Tare Weight</Th>
                    <Th color="white" py={4} px={4} fontSize="sm" fontWeight="600" textTransform="uppercase" letterSpacing="wide">Cyl. Sr. No.</Th>
                    <Th color="white" py={4} px={4} fontSize="sm" fontWeight="600" textTransform="uppercase" letterSpacing="wide">Net Weight</Th>
                    <Th color="white" py={4} px={4} fontSize="sm" fontWeight="600" textTransform="uppercase" letterSpacing="wide">Taxable Amount</Th>
                    <Th color="white" py={4} px={4} fontSize="sm" fontWeight="600" textTransform="uppercase" letterSpacing="wide">IGST Amount</Th>
                    <Th color="white" py={4} px={4} fontSize="sm" fontWeight="600" textTransform="uppercase" letterSpacing="wide">CGST Amount</Th>
                    <Th color="white" py={4} px={4} fontSize="sm" fontWeight="600" textTransform="uppercase" letterSpacing="wide">SGST Amount</Th>
                    <Th color="white" py={4} px={4} fontSize="sm" fontWeight="600" textTransform="uppercase" letterSpacing="wide">Amount</Th>
                    <Th color="white" py={4} px={4} fontSize="sm" fontWeight="600" textTransform="uppercase" letterSpacing="wide">Vehicle No.</Th>
                    <Th color="white" py={4} px={4} fontSize="sm" fontWeight="600" textTransform="uppercase" letterSpacing="wide">Action</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {formik.values.products.map((product, index) => (
                    <Tr key={index} _hover={{ bg: "gray.50" }} borderBottom="1px" borderColor="gray.100" minH="80px">

                      {/* Product - ReadOnly */}
                      <Td py={4} px={4} whiteSpace="nowrap" minH="80px" verticalAlign="top">
                        <FormControl isInvalid={formik.errors.products?.[index]?.product && formik.touched.products?.[index]?.product}>
                          <Input
                            size="md"
                            value={product.product}
                            readOnly
                            bg="gray.50"
                            borderColor="gray.300"
                            textAlign="left"
                            width="auto"
                            minW="max-content"
                          />
                          <Box minH="16px" mt={1}>
                            <FormErrorMessage fontSize="xs">
                              {formik.errors.products?.[index]?.product}
                            </FormErrorMessage>
                          </Box>
                        </FormControl>
                      </Td>

                      {/* Reason - ReadOnly */}
                      <Td py={4} px={4} minW="150px" minH="80px" verticalAlign="top">
                        <FormControl isInvalid={formik.errors.products?.[index]?.reason && formik.touched.products?.[index]?.reason}>
                          <Input
                            size="md"
                            value={product.reason}
                            readOnly
                            bg="gray.50"
                            borderColor="gray.300"
                            textAlign="left"
                          />
                          <Box minH="16px" mt={1}>
                            <FormErrorMessage fontSize="xs">
                              {formik.errors.products?.[index]?.reason}
                            </FormErrorMessage>
                          </Box>
                        </FormControl>
                      </Td>

                      {/* Capacity - Editable */}
                      <Td py={4} px={4} minW="100px" minH="80px" verticalAlign="top">
                        <FormControl isInvalid={formik.errors.products?.[index]?.capacity && formik.touched.products?.[index]?.capacity}>
                          <Input
                            placeholder="Capacity"
                            size="md"
                            value={product.capacity}
                            onChange={(e) => handleInputChange(e, index, "capacity")}
                            onBlur={formik.handleBlur}
                            type="number"
                            step="0.01"
                            bg="white"
                            borderColor="gray.300"
                            textAlign="right"
                            name={`products[${index}].capacity`}
                          />
                          <Box minH="16px" mt={1}>
                            <FormErrorMessage fontSize="xs">
                              {formik.errors.products?.[index]?.capacity}
                            </FormErrorMessage>
                          </Box>
                        </FormControl>
                      </Td>

                      {/* Unit Rate - ReadOnly */}
                      <Td py={4} px={4} minW="100px" minH="80px" verticalAlign="top">
                        <Input
                          placeholder="Unit Rate"
                          size="md"
                          value={product.unit_rate || ""}
                          type="number"
                          bg="gray.50"
                          borderColor="gray.300"
                          textAlign="right"
                          readOnly
                        />
                      </Td>

                      {/* GST Percent - Editable */}
                      <Td py={4} px={4} minW="100px" minH="80px" verticalAlign="top">
                        <FormControl isInvalid={formik.errors.products?.[index]?.gst_percent && formik.touched.products?.[index]?.gst_percent}>
                          <Input
                            placeholder="GST %"
                            size="md"
                            value={product.gst_percent}
                            readOnly
                            bg="gray.50"
                            borderColor="gray.300"
                            textAlign="center"
                            name={`products[${index}].gst_percent`}
                          />
                          <Box minH="16px" mt={1}>
                            <FormErrorMessage fontSize="xs">
                              {formik.errors.products?.[index]?.gst_percent}
                            </FormErrorMessage>
                          </Box>
                        </FormControl>
                      </Td>

                      {/* Return Quantity - ReadOnly */}
                      <Td py={4} px={4} minW="130px" minH="80px" verticalAlign="top">
                        <FormControl>
                          <Input
                            size="md"
                            value={product.return_quantity}
                            readOnly
                            bg="gray.50"
                            borderColor="gray.300"
                            textAlign="center"
                            fontWeight="500"
                          />
                          <Box minH="16px" mt={1}>
                            {/* Empty space for consistency */}
                          </Box>
                        </FormControl>
                      </Td>

                      {/* Cylinder Gross Weight - Editable */}
                      {/* Cylinder Gross Weight - NOW READ ONLY */}
                      <Td py={4} px={4} minW="150px" minH="80px" verticalAlign="top">
                        <FormControl isInvalid={formik.errors.products?.[index]?.cyl_gross_weight && formik.touched.products?.[index]?.cyl_gross_weight}>
                          <Input
                            placeholder="Gross Weight"
                            size="md"
                            value={product.cyl_gross_weight}
                            readOnly  // ← ADD THIS
                            bg="gray.50"  // ← CHANGE from "white" to "gray.50"
                            borderColor="gray.300"
                            textAlign="center"
                            fontWeight="500"  // ← ADD THIS (optional, for consistency)
                          />
                          <Box minH="16px" mt={1}>
                            <FormErrorMessage fontSize="xs">
                              {formik.errors.products?.[index]?.cyl_gross_weight}
                            </FormErrorMessage>
                          </Box>
                        </FormControl>
                      </Td>

                      {/* Cylinder Tare Weight - NOW READ ONLY */}
                      <Td py={4} px={4} minW="150px" minH="80px" verticalAlign="top">
                        <FormControl isInvalid={formik.errors.products?.[index]?.cyl_tare_weight && formik.touched.products?.[index]?.cyl_tare_weight}>
                          <Input
                            placeholder="Tare Weight"
                            size="md"
                            value={product.cyl_tare_weight}
                            readOnly  // ← ADD THIS
                            bg="gray.50"  // ← CHANGE from "white" to "gray.50"
                            borderColor="gray.300"
                            textAlign="center"
                            fontWeight="500"  // ← ADD THIS (optional, for consistency)
                          />
                          <Box minH="16px" mt={1}>
                            <FormErrorMessage fontSize="xs">
                              {formik.errors.products?.[index]?.cyl_tare_weight}
                            </FormErrorMessage>
                          </Box>
                        </FormControl>
                      </Td>

                      {/* Cylinder Serial Number - Editable */}
                      <Td py={4} px={4} minW="130px" minH="80px" verticalAlign="top">
                        <FormControl isInvalid={formik.errors.products?.[index]?.cyl_serial_no && formik.touched.products?.[index]?.cyl_serial_no}>
                          <Input
                            value={product.cyl_serial_no}
                            name={`products[${index}].cyl_serial_no`}
                            isReadOnly
                            bg="gray.100"
                            borderColor="gray.300"
                            placeholder="Serial Number (auto-fetched)"
                          />
                          <Box minH="16px" mt={1}>
                            <FormErrorMessage fontSize="xs">
                              {formik.errors.products?.[index]?.cyl_serial_no}
                            </FormErrorMessage>
                          </Box>
                        </FormControl>
                      </Td>

                      {/* Net Weight - ReadOnly */}
                      <Td py={4} px={4} minW="130px" minH="80px" verticalAlign="top">
                        <FormControl isInvalid={formik.errors.products?.[index]?.net_weight && formik.touched.products?.[index]?.net_weight}>
                          <Input
                            placeholder="Net Weight"
                            size="md"
                            value={product.net_weight}
                            readOnly
                            bg="gray.50"
                            borderColor="gray.200"
                            textAlign="center"
                            fontWeight="500"
                          />
                          <Box minH="16px" mt={1}>
                            <FormErrorMessage fontSize="xs">
                              {formik.errors.products?.[index]?.net_weight}
                            </FormErrorMessage>
                          </Box>
                        </FormControl>
                      </Td>

                      {/* Taxable Amount - ReadOnly */}
                      <Td py={4} px={4} minW="150px" minH="80px" verticalAlign="top">
                        <FormControl isInvalid={formik.errors.products?.[index]?.taxable_amount && formik.touched.products?.[index]?.taxable_amount}>
                          <Input
                            placeholder="Taxable Amount"
                            size="md"
                            value={product.taxable_amount}
                            readOnly
                            bg="gray.50"
                            borderColor="gray.200"
                            textAlign="right"
                            fontWeight="500"
                          />
                          <Box minH="16px" mt={1}>
                            <FormErrorMessage fontSize="xs">
                              {formik.errors.products?.[index]?.taxable_amount}
                            </FormErrorMessage>
                          </Box>
                        </FormControl>
                      </Td>

                      {/* IGST Amount - ReadOnly */}
                      <Td py={4} px={4} minW="130px" minH="80px" verticalAlign="top">
                        <FormControl isInvalid={formik.errors.products?.[index]?.igst_amount && formik.touched.products?.[index]?.igst_amount}>
                          <Input
                            placeholder="IGST Amount"
                            size="md"
                            value={product.igst_amount}
                            readOnly
                            bg="gray.50"
                            borderColor="gray.200"
                            textAlign="right"
                            fontWeight="500"
                          />
                          <Box minH="16px" mt={1}>
                            <FormErrorMessage fontSize="xs">
                              {formik.errors.products?.[index]?.igst_amount}
                            </FormErrorMessage>
                          </Box>
                        </FormControl>
                      </Td>

                      {/* CGST Amount - ReadOnly */}
                      <Td py={4} px={4} minW="130px" minH="80px" verticalAlign="top">
                        <FormControl isInvalid={formik.errors.products?.[index]?.cgst_amount && formik.touched.products?.[index]?.cgst_amount}>
                          <Input
                            placeholder="CGST Amount"
                            size="md"
                            value={product.cgst_amount}
                            readOnly
                            bg="gray.50"
                            borderColor="gray.200"
                            textAlign="right"
                            fontWeight="500"
                          />
                          <Box minH="16px" mt={1}>
                            <FormErrorMessage fontSize="xs">
                              {formik.errors.products?.[index]?.cgst_amount}
                            </FormErrorMessage>
                          </Box>
                        </FormControl>
                      </Td>

                      {/* SGST Amount - ReadOnly */}
                      <Td py={4} px={4} minW="130px" minH="80px" verticalAlign="top">
                        <FormControl isInvalid={formik.errors.products?.[index]?.sgst_amount && formik.touched.products?.[index]?.sgst_amount}>
                          <Input
                            placeholder="SGST Amount"
                            size="md"
                            value={product.sgst_amount}
                            readOnly
                            bg="gray.50"
                            borderColor="gray.200"
                            textAlign="right"
                            fontWeight="500"
                          />
                          <Box minH="16px" mt={1}>
                            <FormErrorMessage fontSize="xs">
                              {formik.errors.products?.[index]?.sgst_amount}
                            </FormErrorMessage>
                          </Box>
                        </FormControl>
                      </Td>

                      {/* Amount - ReadOnly */}
                      <Td py={4} px={4} minW="120px" minH="80px" verticalAlign="top">
                        <FormControl isInvalid={formik.errors.products?.[index]?.amount && formik.touched.products?.[index]?.amount}>
                          <Input
                            placeholder="Amount"
                            size="md"
                            value={product.amount}
                            readOnly
                            bg="gray.50"
                            borderColor="gray.200"
                            textAlign="right"
                            fontWeight="600"
                          />
                          <Box minH="16px" mt={1}>
                            <FormErrorMessage fontSize="xs">
                              {formik.errors.products?.[index]?.amount}
                            </FormErrorMessage>
                          </Box>
                        </FormControl>
                      </Td>

                      {/* Vehicle Number - Editable with Autocomplete */}
                      <Td py={4} px={4} minW="200px" minH="80px" verticalAlign="top">
                        <FormControl isInvalid={formik.errors.products?.[index]?.vehicle_number && formik.touched.products?.[index]?.vehicle_number}>
                          <Box position="relative" w="100%">
                            <Input
                              placeholder="Enter Vehicle No."
                              size="md"
                              value={product.vehicle_number}
                              onChange={(e) => {
                                const updatedProducts = [...formik.values.products];
                                updatedProducts[index].vehicle_number = e.target.value;
                                updatedProducts[index].vehicle_id = "";
                                formik.setFieldValue("products", updatedProducts);
                              }}
                              onBlur={formik.handleBlur}
                              bg="white"
                              borderColor="gray.300"
                              name={`products[${index}].vehicle_number`}
                            />
                            {product.vehicle_number.length > 0 && (
                              <Box
                                position="absolute"
                                top="100%"
                                left={0}
                                right={0}
                                bg="white"
                                border="1px solid #ccc"
                                borderRadius="md"
                                zIndex={10}
                                maxH="150px"
                                overflowY="auto"
                                mt={1}
                              >
                                {vehicle_list
                                  ?.filter((v) =>
                                    (v.vehicleno || "")
                                      .toLowerCase()
                                      .includes((product.vehicle_number || "").toLowerCase())
                                  )
                                  .map((v) => (
                                    <Box
                                      key={v.id}
                                      px={3}
                                      py={2}
                                      _hover={{ bg: "gray.100", cursor: "pointer" }}
                                      onClick={() => {
                                        const updatedProducts = [...formik.values.products];
                                        updatedProducts[index].vehicle_number = v.vehicleno;
                                        updatedProducts[index].vehicle_id = v.id;
                                        formik.setFieldValue("products", updatedProducts);
                                      }}
                                    >
                                      {v.vehicleno}
                                    </Box>
                                  ))}

                                {vehicle_list?.filter((v) =>
                                  (v.vehicleno || "").toLowerCase()
                                    .includes((product.vehicle_number || "").toLowerCase())
                                ).length === 0 && (
                                    <Box px={3} py={2} color="gray.500">
                                      No vehicles found
                                    </Box>
                                  )}
                              </Box>
                            )}
                          </Box>
                          <Box minH="16px" mt={1}>
                            <FormErrorMessage fontSize="xs">
                              {formik.errors.products?.[index]?.vehicle_number}
                            </FormErrorMessage>
                          </Box>
                        </FormControl>
                      </Td>

                      {/* Action - Remove Button */}
                      <Td py={4} px={4} minW="100px" minH="80px" verticalAlign="top">
                        <Box display="flex" flexDirection="column" h="100%">
                          <Button
                            size="sm"
                            colorScheme="red"
                            variant="outline"
                            onClick={() => removeProductRow(index)}
                            isDisabled={formik.values.products.length <= 1}
                            _hover={{ bg: "red.50" }}
                          >
                            Remove
                          </Button>
                          <Box minH="16px" mt={1}>
                            {/* Empty space for consistency */}
                          </Box>
                        </Box>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Box>

          <HStack spacing={4} justify="flex-end" w="100%" pt={4}>
            <Button
              type="button"
              colorScheme="teal"
              onClick={handleCalculateAll}
              size="lg"
              px={8}
              _hover={{ transform: "translateY(-1px)" }}
              shadow="md"
            >
              CALCULATE
            </Button>
          </HStack>
        </VStack>

        <HStack w="100%" justify="space-between" align="flex-start" pt={6}>
          <FormControl w="50%">
            <VStack align="flex-start" spacing={2} w="100%">
              <Text fontSize="sm" fontWeight="500" color="gray.600">
                Refund Payment Mode
              </Text>
              {isOriginalCredit ? (
                <Input
                  value="Credit"
                  isReadOnly
                  bg="gray.100"
                  borderColor="gray.300"
                  color="gray.600"
                  fontWeight="500"
                  size="md"
                  _focus={{ borderColor: "gray.300", boxShadow: "none" }}
                />
              ) : (
                <Select
                  placeholder="Select refund method"
                  value={selectedPaymentMode || ""}
                  onChange={(e) => {
                    setSelectedPaymentMode(e.target.value);
                    handleRefundClick(e.target.value);
                  }}
                >
                  <option value="cash">Cash Refund</option>
                  <option value="online">Online Transfer</option>
                  {/* <option value="wallet">Wallet Credit</option> */}
                </Select>
              )}
            </VStack>
          </FormControl>

          {/* Save button - always visible now */}
          <VStack align="flex-end" spacing={2}>
            <Text fontSize="sm" fontWeight="500" color="transparent">
              Action
            </Text>
            <Button
              colorScheme="blue"
              size="lg"
              px={8}
              py={3}
              onClick={handleSaveRefund}
              isLoading={isProcessing}
              loadingText="Processing..."
              isDisabled={
                !calculatedTotalAmount ||
                calculatedTotalAmount === "0.00" ||
                (!isOriginalCredit && !selectedPaymentMode)
              }
              _hover={{ transform: "translateY(-1px)" }}
              shadow="md"
              leftIcon={<CreditCard size={20} />}
            >
              {isOriginalCredit ? "SAVE CREDIT REFUND" : "SAVE REFUND"}
            </Button>
          </VStack>
        </HStack>

        <RefundProcess
          isOpen={isPopupOpen}
          onClose={() => setIsPopupOpen(false)}
          returnData={currentReturnData}
          calculatedAmount={calculatedTotalAmount}
          onRefundComplete={(method, amount, bankId, transactionNum, transMode, notes) => {
            setSelectedBankId(bankId);
            setTransactionNumber(transactionNum);
            setTransactionMode(transMode);
            setPaymentNotes(notes);
            setIsPopupOpen(false);
          }}
          mode={selectedMode}
          formData={formik.values}
          initialStep={initialRefundStep}
        />
      </Card>
      {/* Confirmation Dialog */}
      <AlertDialog
        isOpen={showConfirmation}
        leastDestructiveRef={cancelRef}
        onClose={handleCancelRefund}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirm Refund Processing
            </AlertDialogHeader>

            <AlertDialogBody>
              <VStack align="start" spacing={3}>
                <Text>
                  Are you sure you want to process this refund?
                </Text>
                <Box bg="gray.50" p={4} borderRadius="md" w="100%">
                  <VStack align="start" spacing={2}>
                    <HStack justify="space-between" w="100%">
                      <Text fontWeight="semibold">Payment Method:</Text>
                      <Text color="blue.600" fontWeight="bold">
                        {confirmationData?.mode === 'credit' ? 'Credit' :
                          confirmationData?.mode === 'cash' ? 'Cash' :
                            confirmationData?.mode === 'online' ? 'Online Transfer' :
                              confirmationData?.mode === 'wallet' ? 'Wallet Credit' : 'Unknown'}
                      </Text>
                    </HStack>
                    <HStack justify="space-between" w="100%">
                      <Text fontWeight="semibold">Refund Amount:</Text>
                      <Text color="green.600" fontWeight="bold">
                        ₹{calculatedTotalAmount}
                      </Text>
                    </HStack>
                    {confirmationData?.bankId && (
                      <HStack justify="space-between" w="100%">
                        <Text fontWeight="semibold">Bank ID:</Text>
                        <Text>{confirmationData.bankId}</Text>
                      </HStack>
                    )}
                    {confirmationData?.transactionNum && (
                      <HStack justify="space-between" w="100%">
                        <Text fontWeight="semibold">Transaction Number:</Text>
                        <Text>{confirmationData.transactionNum}</Text>
                      </HStack>
                    )}
                  </VStack>
                </Box>
                <Text fontSize="sm" color="red.500" fontWeight="medium">
                  This action cannot be undone.
                </Text>
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={handleCancelRefund}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleConfirmRefund}
                ml={3}
                isLoading={isProcessing}
                loadingText="Processing..."
              >
                Yes, Process Refund
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default EditDefectiveReturn;    